import React, { useState } from "react";
import "./BookingPage.css";
import { questions } from "../../utils/bookingQuestions";
import { validateInput } from "../../utils/validation";
import emailjs from "@emailjs/browser"; // Import EmailJS
import usePageMeta from "../../hooks/usePageMeta";

function BookingPage() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [isExiting, setIsExiting] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  usePageMeta(
    "Book a Session | TjWit Media",
    "Book a professional photography session for sports, portraits, real estate, and more. Serving Tampa and St. Louis."
  );

  const photoTypes = [
    "Sports",
    "Headshots",
    "Portraits",
    "Real Estate",
    "Commercial",
    "Events",
    "Drone/Ariel",
    "Other",
  ];

  const getQuestionForStep = (stepIndex, currentFormData) => {
    if (stepIndex === 0) return null;
    const photoBranch = currentFormData.photoType
      ? questions[currentFormData.photoType]
      : [];
    if (stepIndex > 0 && stepIndex <= photoBranch.length) {
      return photoBranch[stepIndex - 1];
    }
    const contactStepIndex = stepIndex - photoBranch.length;
    if (
      contactStepIndex > 0 &&
      contactStepIndex <= questions.contactInfo.length
    ) {
      return questions.contactInfo[contactStepIndex - 1];
    }
    return null;
  };

  const currentQuestion = getQuestionForStep(step, formData);
  const isLastQuestion =
    currentQuestion &&
    currentQuestion.id ===
      questions.contactInfo[questions.contactInfo.length - 1].id;

  const handleStateTransition = (nextStep, field, value) => {
    const newFormData = { ...formData };
    if (field && value !== undefined) {
      newFormData[field] = value;
    }

    if (nextStep > step) {
      const questionToValidate =
        step === 0 ? { type: "choice" } : currentQuestion;
      const valueToValidate =
        questionToValidate.type === "choice" ? value : currentAnswer;
      const err = validateInput(questionToValidate, valueToValidate);
      if (err) {
        setError(err);
        return;
      }
    }

    setError(null);
    setIsExiting(true);

    setTimeout(() => {
      setFormData(newFormData);

      const totalQuestions =
        (newFormData.photoType ? questions[newFormData.photoType].length : 0) +
        questions.contactInfo.length;
      if (nextStep > totalQuestions) {
        setIsSubmitting(true);
        emailjs
          .send(
            "service_jtd83vf", // <-- Paste your Service ID here
            "template_n6099vr", // <-- Paste your Template ID here
            newFormData,
            "uDHtkIMnNaTr6pJ9Z" // <-- Paste your Public Key here
          )
          .then(
            (result) => {
              console.log(result.text);
              setIsSubmitting(false);
              setIsSuccess(true);
            },
            (error) => {
              console.log(error.text);
              setError("Failed to send message. Please try again.");
              setIsSubmitting(false);
            }
          );
      } else {
        setStep(nextStep);
        const nextQuestion = getQuestionForStep(nextStep, newFormData);
        setCurrentAnswer(newFormData[nextQuestion?.id] || "");
        setIsExiting(false);
      }
    }, 500);
  };

  const handleNext = () => {
    handleStateTransition(step + 1, currentQuestion.id, currentAnswer);
  };

  const handlePrev = () => {
    // A simplified transition for going back
    setIsExiting(true);
    setTimeout(() => {
      setStep(step - 1);
      const prevQuestion = getQuestionForStep(step - 1, formData);
      setCurrentAnswer(formData[prevQuestion?.id] || "");
      setIsExiting(false);
    }, 500);
  };

  const renderInput = () => {
    // ... This function remains unchanged ...
    if (!currentQuestion) return null;
    switch (currentQuestion.type) {
      case "text":
      case "date":
      case "number":
        return (
          <input
            type={currentQuestion.type}
            className="input-field"
            placeholder={currentQuestion.placeholder}
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
          />
        );
      case "choice":
        return (
          <div className="options-grid">
            {" "}
            {currentQuestion.options.map((opt) => (
              <button
                type="button"
                key={opt}
                className="option-button"
                onClick={() =>
                  handleStateTransition(step + 1, currentQuestion.id, opt)
                }
              >
                {" "}
                {opt}{" "}
              </button>
            ))}{" "}
          </div>
        );
      default:
        return null;
    }
  };

  if (isSuccess) {
    return (
      <div className="booking-container">
        <div className="question-wrapper question-enter-active">
          <h2 className="question-text">
            Thank you! Your booking request has been sent.
          </h2>
          <p>I'll be in touch shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => e.preventDefault()} className="booking-container">
      <div
        className={`question-wrapper ${
          isExiting ? "question-exit-active" : "question-enter-active"
        }`}
      >
        {step === 0 ? (
          <>
            <h2 className="question-text">
              What type of photography are you looking for?
            </h2>
            <div className="options-grid">
              {" "}
              {photoTypes.map((type) => (
                <button
                  type="button"
                  key={type}
                  className="option-button"
                  onClick={() =>
                    handleStateTransition(step + 1, "photoType", type)
                  }
                >
                  {" "}
                  {type}{" "}
                </button>
              ))}{" "}
            </div>
          </>
        ) : currentQuestion ? (
          <>
            <h2 className="question-text">{currentQuestion.text}</h2>
            {renderInput()}
            <div className="error-message">{error}</div>
            {currentQuestion.type !== "choice" && (
              <div className="nav-buttons">
                <button
                  type="button"
                  className="nav-button back"
                  onClick={handlePrev}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="nav-button"
                  onClick={handleNext}
                  disabled={isSubmitting}
                >
                  {isLastQuestion
                    ? isSubmitting
                      ? "Sending..."
                      : "Submit"
                    : "Next"}
                </button>
              </div>
            )}
          </>
        ) : (
          !isSubmitting && <h2 className="question-text">Loading...</h2>
        )}
      </div>
    </form>
  );
}

export default BookingPage;
